import { useQueryClient } from '@tanstack/react-query';
import { type LabelInValue, type VideoContentObject, createVideoContentObject } from 'models';
import { useMutation } from 'query';
import type { FileUploadDone } from 'ui';
import { updateVideo } from '../../services';
import { getVideoDetailQueryKey, getVideosQueryKey } from '../keys';

type UpdateVideoVariables = {
  data: {
    coverPhoto?: FileUploadDone;
    featuredInChannels?: boolean;
    full?: { externalId?: string };
    htmlContent?: string;
    name?: string;
    recordedAt?: Date;
    showInChannels?: boolean;
    tags?: LabelInValue[];
    textContent?: string;
    trailer?: { externalId?: string };
  };
  id: string;
};

export const useUpdateVideoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<VideoContentObject, UpdateVideoVariables>({
    async mutationFn({
      data: {
        coverPhoto,
        featuredInChannels,
        full,
        htmlContent,
        name,
        recordedAt,
        showInChannels,
        tags,
        textContent,
        trailer,
      },
      id,
    }) {
      const response = await updateVideo({
        data: {
          video: {
            cover_photo: coverPhoto?.response,
            featured_in_channels: featuredInChannels,
            full: full ? { external_id: full.externalId } : undefined,
            html_content: htmlContent,
            name,
            recorded_at: recordedAt?.toISOString(),
            show_in_channels: showInChannels,
            tag_ids: tags?.map(({ value }) => value),
            text_content: textContent,
            trailer: trailer ? { external_id: trailer.externalId } : undefined,
          },
        },
        id,
      });

      return createVideoContentObject(response.data);
    },
    async onSuccess({ video }) {
      await queryClient.invalidateQueries({ queryKey: getVideoDetailQueryKey({ videoId: video.id }) });
      await queryClient.invalidateQueries({ queryKey: getVideosQueryKey({}) });
    },
  });
};
